<script setup lang="ts">
import { ChartBar, ChevronRight, ChevronsUpDown, CirclePlus, LogOut, Workflow } from 'lucide-vue-next'
import { storeToRefs } from 'pinia'
import { computed, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import AtsLogo from '@/assets/ATS-logo.svg?component'
import DarkAtsLogo from '@/assets/ATS-logo--dark.svg?component'
import NotificationDropdown from '@/components/NotificationDropdown.vue'
import { useAuth } from '@/composables'
import { useColorMode } from '@/composables/src/useColorMode'
import { Avatar, AvatarFallback, AvatarImage } from '@/foundation/avatar'
import { Button } from '@/foundation/button'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/foundation/collapsible'
import { Dialog, DialogContent, DialogFooter, DialogHeader } from '@/foundation/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/foundation/dropdown-menu'
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarRail,
  SidebarTrigger,
  useSidebar,
} from '@/foundation/sidebar'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/foundation/tooltip'
import { useAccountStore } from '@/stores/account'
import { useQueuesStore } from '@/stores/queues'
import CreateTicketQueueForm from '@/views/admin/components/queue/CreateQueueForm.vue'
// This is sample data.
import { data } from './platform-links'

const { logout } = useAuth()
const accountStore = useAccountStore()
const { isClientUser } = accountStore
const { queues } = storeToRefs(useQueuesStore())

const { mode } = useColorMode()

const user = computed(() => {
  const user = accountStore.user
  const username = user.name && user.name.trim() !== '' ? user.name : user.username

  return {
    username,
    email: user.email,
    avatar: user.avatar,
    usernameInitials: username?.substring(0, 2).toUpperCase(),
  }
})

const mvp = ref(false)
const route = useRoute()
const router = useRouter()
const isDialogOpen = ref(false)
const openCollapsible = ref<string | null>(null)

const openCreatedDialog = () => {
  isDialogOpen.value = false
}

const handleCollapsibleToggle = (itemTitle: string, isOpen: boolean) => {
  if (isOpen) {
    openCollapsible.value = itemTitle
  } else if (openCollapsible.value === itemTitle) {
    if (itemTitle === 'Tickets' || itemTitle === 'Queues') {
      const currentPath = route.path

      let isOnRelevantQueueRoute = false

      if (itemTitle === 'Tickets') {
        isOnRelevantQueueRoute = queues.value.some((queue) => currentPath === `/queues/${queue.slug}`)
      } else if (itemTitle === 'Queues') {
        isOnRelevantQueueRoute = queues.value.some((queue) => currentPath === `/settings/queue/${queue.id}`)
      }

      if (isOnRelevantQueueRoute) {
        return
      }
    }

    openCollapsible.value = null
  }
}

const isCollapsed = computed(() => state.value === 'collapsed')

const sideLinks = computed(() => {
  let links = []

  if (route.name === 'settings') {
    links = data.navMain.filter(
      (item) => item.title.includes('Config') || item.title === 'Queues' || item.title === 'SLA' || item.title === 'Workflow',
    )
  } else if (!accountStore.isAdminUser) {
    const isExternalUser = accountStore.isClientUser || accountStore.isClientManager || accountStore.isClientApprover

    links = data.navMain
      .filter((item) => item.title !== 'Settings')
      .map((item) => {
        if (item.items && item.title === 'Insights' && isExternalUser) {
          return {
            ...item,
            items: item.items.filter((subItem) => subItem.title !== 'Ticket Insights'),
          }
        }

        return item
      })
  } else if (accountStore.isClientUser) {
    const canSeeInsights = accountStore.isClientApprover || accountStore.isClientManager
    const isExternalUser = true

    links = data.navMain
      .filter((item) => canSeeInsights || item.title !== 'Insights')
      .map((item) => {
        if (item.items && item.title === 'Insights' && isExternalUser) {
          return {
            ...item,
            items: item.items.filter((subItem) => subItem.title !== 'Ticket Insights'),
          }
        }

        if (item.items) {
          return {
            ...item,
            items: item.items.filter((subItem) => subItem.title !== 'Assigned to Me' && subItem.title !== 'Unassigned'),
          }
        }

        return item
      })
  } else {
    links = data.navMain
  }

  if (!mvp.value) {
    return links
  }

  return links.filter((group) => !group.future)
})

const handleMainTabClick = (item: any) => {
  if (item.title === 'Queues' && queues.value.length > 0) {
    const firstQueue = queues.value.find((queue) => queue?.show !== false)

    if (firstQueue) {
      router.push(`/settings/queue/${firstQueue.id}`)

      return
    }
  }

  if (item.items && item.items.length > 0) {
    router.push(item.items[0].url)
  }
}

const initializeOpenCollapsible = () => {
  const currentPath = route.path

  for (const item of sideLinks.value) {
    if (item.items && item.type !== 'section-title') {
      const hasActiveSubItem = item.items.some((subItem: any) => currentPath === subItem.url)

      if (item.title === 'Tickets' || item.title === 'Queues') {
        const hasActiveQueue = queues.value.some(
          (queue) => currentPath === `/queues/${queue.slug}` || currentPath === `/settings/queue/${queue.id}`,
        )

        if (hasActiveQueue) {
          if (item.title === 'Tickets' && queues.value.some((queue) => currentPath === `/queues/${queue.slug}`)) {
            openCollapsible.value = item.title

            return
          } else if (item.title === 'Queues' && queues.value.some((queue) => currentPath === `/settings/queue/${queue.id}`)) {
            openCollapsible.value = item.title

            return
          }
        }
      }

      if (hasActiveSubItem) {
        openCollapsible.value = item.title

        return
      }
    }
  }
}

watch(() => route.path, initializeOpenCollapsible, { immediate: true })
watch(() => queues.value, initializeOpenCollapsible, { immediate: false, deep: true })

const { state } = useSidebar()
</script>

<template>
  <Sidebar collapsible="icon">
    <SidebarHeader>
      <div class="flex items-center justify-between gap-2 px-2 mt-3">
        <RouterLink to="/">
          <div v-if="!isCollapsed">
            <DarkAtsLogo v-if="mode === 'dark'" alt="Logo" class="w-20" />
            <AtsLogo v-if="mode === 'light'" alt="Logo" class="w-20" />
          </div>
        </RouterLink>
        <TooltipProvider v-if="!isClientUser">
          <Tooltip>
            <TooltipTrigger as-child :class="isCollapsed ? 'mr-2' : '-mr-1'">
              <SidebarTrigger />
            </TooltipTrigger>
            <TooltipContent side="bottom">Toggle sidebar navigation</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    </SidebarHeader>
    <SidebarContent>
      <SidebarGroup>
        <SidebarMenu>
          <SidebarMenuButton v-if="route.name !== 'settings' && mvp" as-child tooltip="Insights" class="pt-2.5 cursor-pointer">
            <RouterLink to="/dashboard" class="flex justify-between items-center w-full">
              <ChartBar class="size-6" />
              <span class="flex-1">Overview</span>
            </RouterLink>
          </SidebarMenuButton>
          <template v-for="item in sideLinks" :key="item.title">
            <div v-if="item.type === 'section-title' && !isCollapsed" class="px-3 mt-3 mb-2 first:mt-0">
              <h3 class="font-sans font-medium text-xs leading-4 tracking-normal text-muted-foreground uppercase">
                {{ item.title }}
              </h3>
            </div>
            <Collapsible
              v-else-if="item.items && item.type !== 'section-title'"
              v-slot="{ open }"
              as-child
              :open="openCollapsible === item.title"
              class="group/collapsible"
              @update:open="(isOpen) => handleCollapsibleToggle(item.title, isOpen)"
            >
              <SidebarMenuItem>
                <CollapsibleTrigger as-child>
                  <SidebarMenuButton
                    :tooltip="item.title"
                    :class="[
                      'min-h-9 flex items-center h-10',
                      isCollapsed ? 'justify-center !w-12 !rounded-full mx-auto my-1' : 'justify-start',
                    ]"
                    @click="handleMainTabClick(item)"
                  >
                    <div class="flex items-center h-full w-full gap-2">
                      <span
                        class="flex items-center justify-center transition-all rounded-full border border-solid border-[var(--base-border,#E4E4E7)] dark:!border-none shrink-0 w-9 h-9"
                        :style="{
                          background:
                            'linear-gradient(0deg,rgba(137,137,137,0.05),rgba(137,137,137,0.05)),radial-gradient(42.5% 42.5% at 50% 82.5%,rgba(116,156,255,0.2) 0%,rgba(116,156,255,0) 100%)',
                        }"
                      >
                        <component :is="item.icon" class="w-4 h-4" />
                      </span>
                      <span
                        v-if="!isCollapsed"
                        class="flex-1 font-sans font-normal not-italic text-sm leading-5 tracking-normal"
                      >
                        {{ item.title }}
                      </span>
                      <Button
                        v-if="item.title === 'Queues' && !isCollapsed && open"
                        variant="ghost"
                        size="sm"
                        class="ml-auto p-1 h-6 w-6 rounded-full hover:bg-muted"
                        @click.stop="isDialogOpen = true"
                      >
                        <CirclePlus class="w-4 h-4" />
                      </Button>
                      <ChevronRight
                        v-else-if="item.items && (item.title !== 'Queues' || !open || isCollapsed)"
                        class="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90"
                        :size="16"
                      />
                    </div>
                  </SidebarMenuButton>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <SidebarMenuSub>
                    <SidebarMenuSubItem v-for="subItem in item.items" :key="subItem.title">
                      <SidebarMenuSubButton
                        as-child
                        :is-active="route.path === subItem.url"
                        :class="subItem.disabled && 'opacity-50 cursor-not-allowed'"
                      >
                        <RouterLink v-if="route.path !== subItem.url" :to="subItem.url" class="flex justify-between w-full">
                          <span>{{ subItem.title }}</span>
                        </RouterLink>
                        <div v-else class="w-full">
                          <div class="flex !justify-between w-full">
                            <span>{{ subItem.title }}</span>
                          </div>
                        </div>
                      </SidebarMenuSubButton>
                    </SidebarMenuSubItem>
                    <template v-if="item.title === 'Tickets'">
                      <SidebarMenuSubItem v-for="subItem in queues.filter((queue) => queue?.show !== false)" :key="subItem.id">
                        <SidebarMenuSubButton as-child :is-active="route.path === `/queues/${subItem.slug}`">
                          <RouterLink
                            v-if="route.path !== `/queues/${subItem.slug}`"
                            :to="`/queues/${subItem.slug}`"
                            class="flex justify-between w-full"
                          >
                            <span>{{ subItem.title }}</span>
                          </RouterLink>
                          <div v-else class="w-full">
                            <div class="flex justify-between w-full">
                              <span>{{ subItem.title }}</span>
                            </div>
                          </div>
                        </SidebarMenuSubButton>
                      </SidebarMenuSubItem>
                    </template>
                    <template v-if="item.title === 'Queues'">
                      <SidebarMenuSubItem v-for="subItem in queues" :key="subItem.id">
                        <SidebarMenuSubButton as-child :is-active="route.path === `/settings/queue/${subItem.id}`">
                          <RouterLink
                            v-if="route.path !== `/settings/queue/${subItem.id}`"
                            :to="`/settings/queue/${subItem.id}`"
                            class="flex justify-between w-full"
                          >
                            <span>{{ subItem.title }}</span>
                          </RouterLink>
                          <div v-else class="w-full">
                            <div class="flex justify-between w-full">
                              <span>{{ subItem.title }}</span>
                            </div>
                          </div>
                        </SidebarMenuSubButton>
                      </SidebarMenuSubItem>
                    </template>
                  </SidebarMenuSub>
                </CollapsibleContent>
              </SidebarMenuItem>
            </Collapsible>
            <SidebarMenuItem v-else-if="item.type !== 'section-title'">
              <SidebarMenuButton
                :tooltip="item.title"
                :class="[
                  ' min-h-9 flex items-center h-10',
                  { 'bg-sidebar-accent': route.path === item.url },
                  isCollapsed ? 'justify-center !w-12 !rounded-full mx-auto my-1' : 'justify-start',
                ]"
              >
                <RouterLink :to="item.url" class="flex items-center h-full w-full">
                  <span
                    class="flex items-center justify-center transition-all rounded-full border border-solid border-[var(--base-border,#E4E4E7)] dark:!border-none shrink-0 w-9 h-9"
                    :style="{
                      background:
                        'linear-gradient(0deg,rgba(137,137,137,0.05),rgba(137,137,137,0.05)),radial-gradient(42.5% 42.5% at 50% 82.5%,rgba(116,156,255,0.2) 0%,rgba(116,156,255,0) 100%)',
                    }"
                  >
                    <component :is="item.icon" class="w-4 h-4" />
                  </span>
                  <span
                    v-if="!isCollapsed"
                    class="flex-1 font-sans font-normal not-italic !text-sm leading-5 tracking-normal ml-2"
                  >
                    {{ item.title }}
                  </span>
                </RouterLink>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </template>
          <SidebarMenuButton
            v-if="route.name !== 'settings' && mvp"
            as-child
            tooltip="Self Service"
            class="pt-2.5 cursor-pointer"
          >
            <RouterLink to="/" class="flex justify-between items-center w-full">
              <Workflow class="size-6" />
              <span class="flex-1">Quick Actions</span>
            </RouterLink>
          </SidebarMenuButton>
        </SidebarMenu>
      </SidebarGroup>
    </SidebarContent>
    <SidebarFooter>
      <SidebarMenu>
        <SidebarMenuItem>
          <NotificationDropdown>
            <template #trigger="{ unreadCount }">
              <SidebarMenuButton
                size="lg"
                class="relative hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                :class="{ 'ml-1.5': isCollapsed }"
              >
                <div class="flex items-center gap-3 w-full">
                  <div class="relative">
                    <div class="flex h-8 w-8 items-center justify-center rounded-lg bg-muted dark:bg-muted">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        class="h-4 w-4"
                      >
                        <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
                        <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
                      </svg>
                    </div>
                    <span
                      v-if="unreadCount > 0"
                      class="absolute -top-0.5 -right-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[9px] font-bold text-white ring-1 ring-background dark:ring-background"
                    >
                      {{ unreadCount }}
                    </span>
                  </div>
                  <div v-if="!isCollapsed" class="grid flex-1 text-left text-sm leading-tight">
                    <span class="truncate font-normal">Notifications</span>
                  </div>
                  <svg
                    v-if="!isCollapsed"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    class="ml-auto h-4 w-4"
                  >
                    <path d="m9 18 6-6-6-6" />
                  </svg>
                </div>
              </SidebarMenuButton>
            </template>
          </NotificationDropdown>
        </SidebarMenuItem>

        <SidebarMenuItem>
          <DropdownMenu>
            <DropdownMenuTrigger as-child>
              <SidebarMenuButton
                size="lg"
                class="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
                :class="{ 'ml-1.5': isCollapsed }"
              >
                <Avatar class="h-8 w-8 rounded-lg">
                  <AvatarImage :src="user.avatar ?? ''" :alt="user.username" />
                  <AvatarFallback class="rounded-lg"> {{ user.usernameInitials }} </AvatarFallback>
                </Avatar>
                <div class="grid flex-1 text-left text-sm leading-tight">
                  <span class="truncate font-semibold">{{ user.username }}</span>
                  <span class="truncate text-xs">{{ user.email }}</span>
                </div>
                <ChevronsUpDown class="ml-auto size-4" />
              </SidebarMenuButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              class="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
              side="bottom"
              align="end"
              :side-offset="4"
            >
              <DropdownMenuLabel class="p-0 font-normal">
                <div class="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                  <Avatar class="h-8 w-8 rounded-lg">
                    <AvatarImage :src="user.avatar ?? ''" :alt="user.username" />
                    <AvatarFallback class="rounded-lg"> {{ user.usernameInitials }} </AvatarFallback>
                  </Avatar>
                  <div class="grid flex-1 text-left text-sm leading-tight">
                    <span class="truncate font-semibold">{{ user.username }}</span>
                    <span class="truncate text-xs">{{ user.email }}</span>
                  </div>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuGroup> </DropdownMenuGroup>
              <DropdownMenuItem class="cursor-pointer" @click="logout">
                <LogOut />
                Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </SidebarMenuItem>
      </SidebarMenu>
    </SidebarFooter>
    <SidebarRail />
  </Sidebar>

  <!-- Dialog pour créer une nouvelle queue -->
  <Dialog v-model:open="isDialogOpen">
    <DialogContent class="w-full max-w-3xl grid-rows-[auto_minmax(0,1fr)_auto] max-h-[80dvh] p-0">
      <DialogHeader class="p-3 border-b">
        <h2 class="text-xl font-bold">Create New Queue</h2>
      </DialogHeader>
      <CreateTicketQueueForm
        class="w-full p-4 px-6 overflow-y-auto"
        @create-success="openCreatedDialog"
        @close-dialog="() => (isDialogOpen = false)"
      />
      <DialogFooter class="flex w-full justify-start space-x-4 border-t p-3"> </DialogFooter>
    </DialogContent>
  </Dialog>
</template>
